import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  BarChart3, ArrowLeft, Save, RotateCcw, Settings, 
  TrendingUp, Users, Target, Clock, RefreshCw, 
  AlertTriangle, CheckCircle, Info, Lightbulb
} from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';

const API = process.env.REACT_APP_BACKEND_URL;

const AIDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [config, setConfig] = useState(null);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [insights, setInsights] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    if (config?.enabled) {
      fetchInsights();
    }
  }, [config?.enabled, timeRange]);

  useEffect(() => {
    if (config && originalConfig) {
      setHasChanges(JSON.stringify(config) !== JSON.stringify(originalConfig));
    }
  }, [config, originalConfig]);

  const fetchConfig = async () => {
    try {
      const token = sessionStorage.getItem('auth_token');
      const response = await axios.get(`${API}/api/ai-intelligence/modules/ai_dashboard`, {
        params: { token }
      });
      
      if (response.data.success) {
        setConfig(response.data.module);
        setOriginalConfig(JSON.parse(JSON.stringify(response.data.module)));
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const fetchInsights = async () => {
    setLoadingInsights(true);
    try {
      const token = sessionStorage.getItem('auth_token');
      const response = await axios.get(`${API}/api/ai-intelligence/dashboard/insights`, {
        params: { token, time_range: timeRange }
      });
      
      if (response.data.success) {
        setInsights(response.data.insights);
      }
    } catch (error) {
      console.error('Error fetching insights:', error);
    } finally {
      setLoadingInsights(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('auth_token');
      const response = await axios.put(
        `${API}/api/ai-intelligence/modules/ai_dashboard?token=${token}`,
        {
          enabled: config.enabled,
          settings: config.settings
        }
      );
      
      if (response.data.success) {
        setOriginalConfig(JSON.parse(JSON.stringify(config)));
        toast.success('Configuration saved successfully');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      const token = sessionStorage.getItem('auth_token');
      await axios.post(`${API}/api/ai-intelligence/modules/ai_dashboard/reset?token=${token}`);
      toast.success('Reset to defaults');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to reset');
    }
  };

  const updateSetting = (key, value) => {
    setConfig(prev => ({
      ...prev,
      settings: { ...prev.settings, [key]: value }
    }));
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="ai-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/ai-intelligence')}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl">
              <BarChart3 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">AI Insights Dashboard</h1>
              <p className="text-sm text-slate-500">Data-driven sales intelligence</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32" data-testid="time-range">
              <SelectValue placeholder="Time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="60d">Last 60 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            size="sm"
            onClick={fetchInsights}
            disabled={loadingInsights}
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${loadingInsights ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          <Switch
            checked={config?.enabled}
            onCheckedChange={(checked) => setConfig(prev => ({ ...prev, enabled: checked }))}
            data-testid="module-toggle"
          />
        </div>
      </div>

      {!config?.enabled && (
        <Alert className="bg-amber-50 border-amber-200">
          <Info className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            Dashboard is disabled. Enable it to view AI insights.
          </AlertDescription>
        </Alert>
      )}

      {config?.enabled && insights && (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500 rounded-lg">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-600">Total Leads</p>
                    <p className="text-2xl font-bold text-blue-900">{insights.summary?.total_leads || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500 rounded-lg">
                    <Target className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-green-600">Conversions</p>
                    <p className="text-2xl font-bold text-green-900">{insights.summary?.conversions || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-purple-600">Conversion Rate</p>
                    <p className="text-2xl font-bold text-purple-900">{insights.summary?.conversion_rate || 0}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-500 rounded-lg">
                    <Clock className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-orange-600">Time Range</p>
                    <p className="text-2xl font-bold text-orange-900">{timeRange}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* AI Recommendations */}
          {insights.recommendations && insights.recommendations.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-amber-500" />
                  AI Recommendations
                </CardTitle>
                <CardDescription>
                  Actionable insights to improve performance
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {insights.recommendations.map((rec, index) => (
                  <div 
                    key={index}
                    className={`p-4 rounded-lg border-l-4 ${
                      rec.type === 'warning' 
                        ? 'bg-amber-50 border-amber-500' 
                        : 'bg-blue-50 border-blue-500'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {rec.type === 'warning' ? (
                        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                      ) : (
                        <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                      )}
                      <div>
                        <p className={`font-medium ${rec.type === 'warning' ? 'text-amber-800' : 'text-blue-800'}`}>
                          {rec.title}
                        </p>
                        <p className={`text-sm ${rec.type === 'warning' ? 'text-amber-700' : 'text-blue-700'}`}>
                          {rec.description}
                        </p>
                        {rec.action && (
                          <p className={`text-sm mt-1 font-medium ${rec.type === 'warning' ? 'text-amber-900' : 'text-blue-900'}`}>
                            Action: {rec.action}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Source Performance */}
          {insights.source_performance && insights.source_performance.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-pink-600" />
                  Source Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {insights.source_performance.slice(0, 5).map((source, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className="w-32 text-sm font-medium truncate">{source.source}</div>
                      <div className="flex-1">
                        <div className="h-6 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-pink-500 to-pink-600 rounded-full flex items-center justify-end pr-2"
                            style={{ width: `${Math.min(100, source.conversion_rate * 10)}%` }}
                          >
                            <span className="text-xs text-white font-medium">{source.conversion_rate}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-slate-500 w-20 text-right">
                        {source.leads} leads
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Agent Rankings */}
          {insights.agent_rankings && insights.agent_rankings.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5 text-pink-600" />
                  Agent Performance Rankings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {insights.agent_rankings.slice(0, 5).map((agent, index) => (
                    <div key={index} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        index === 0 ? 'bg-amber-500 text-white' :
                        index === 1 ? 'bg-slate-400 text-white' :
                        index === 2 ? 'bg-orange-600 text-white' :
                        'bg-slate-200 text-slate-600'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">Agent {agent.agent_id?.slice(-6) || 'Unknown'}</p>
                        <p className="text-xs text-slate-500">
                          {agent.assigned} leads • {agent.converted} converted
                        </p>
                      </div>
                      <Badge 
                        variant="outline"
                        className={`${
                          agent.conversion_rate >= 10 ? 'bg-green-50 text-green-700 border-green-200' :
                          agent.conversion_rate >= 5 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                          'bg-slate-50 text-slate-600'
                        }`}
                      >
                        {agent.conversion_rate}%
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Dashboard Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-5 w-5 text-pink-600" />
            Dashboard Settings
          </CardTitle>
          <CardDescription>
            Configure what to show on the dashboard
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <Label className="text-sm">Agent Rankings</Label>
              <Switch
                checked={config?.settings?.show_agent_rankings}
                onCheckedChange={(checked) => updateSetting('show_agent_rankings', checked)}
                data-testid="show-agent-rankings"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <Label className="text-sm">Source Performance</Label>
              <Switch
                checked={config?.settings?.show_source_performance}
                onCheckedChange={(checked) => updateSetting('show_source_performance', checked)}
                data-testid="show-source-performance"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <Label className="text-sm">Conversion Funnel</Label>
              <Switch
                checked={config?.settings?.show_conversion_funnel}
                onCheckedChange={(checked) => updateSetting('show_conversion_funnel', checked)}
                data-testid="show-funnel"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <Label className="text-sm">AI Recommendations</Label>
              <Switch
                checked={config?.settings?.show_recommendations}
                onCheckedChange={(checked) => updateSetting('show_recommendations', checked)}
                data-testid="show-recommendations"
              />
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <Label className="text-sm">Compare to Previous Period</Label>
            <Switch
              checked={config?.settings?.compare_to_previous_period}
              onCheckedChange={(checked) => updateSetting('compare_to_previous_period', checked)}
              data-testid="compare-previous"
            />
          </div>

          <div>
            <Label className="mb-2 block">Default Time Range</Label>
            <Select
              value={config?.settings?.default_time_range}
              onValueChange={(value) => updateSetting('default_time_range', value)}
            >
              <SelectTrigger data-testid="default-time-range">
                <SelectValue placeholder="Select default" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="60d">Last 60 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button
          variant="outline"
          onClick={handleReset}
          className="text-slate-600"
          data-testid="reset-btn"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset to Defaults
        </Button>
        
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="bg-pink-600 hover:bg-pink-700"
          data-testid="save-btn"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Configuration
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AIDashboard;
